
import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Portfolio } from '@/schemas/portfolio'; // Updated import
import { Business } from '@/schemas/business';   // Added import
import PortfolioSection from '@/components/PortfolioSection';
import EmptyPortfolioContent from '@/components/EmptyPortfolioContent';
import { FilterOptions } from '@/components/FilterModal';
import { useIsMobile } from '@/hooks/use-mobile';
import { LayoutMode } from '@/hooks/usePortfolioState';
import { Separator } from '@/components/ui/separator';
import { useBusinessUtils } from '@/hooks/useBusinessUtils';
import { PlusCircle } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { useNoPortfolioBusinesses } from '@/hooks/portfolio/useNoPortfolioBusinesses';
import { useFirebase } from '@/context/FirebaseProvider'; // Added import

interface PortfolioListProps {
  filteredPortfolios: Portfolio[];
  allBusinesses: Business[]; // Added allBusinesses prop
  searchTerm: string;
  filters: FilterOptions;
  layoutMode: LayoutMode;
  setSearchTerm: (term: string) => void;
  setFilters: (filters: FilterOptions) => void;
  setShowMobileSearch: (show: boolean) => void;
  setShowFilterButton: (show: boolean) => void;
  setShowFilterOptions: (show: boolean) => void;
  showFilterOptions: boolean;
  onAddNewPortfolio?: () => void;
  className?: string; 
}

const PortfolioList: React.FC<PortfolioListProps> = ({
  filteredPortfolios,
  allBusinesses, // Destructure allBusinesses
  searchTerm,
  filters,
  layoutMode,
  setSearchTerm,
  setFilters,
  setShowMobileSearch,
  setShowFilterButton,
  setShowFilterOptions,
  showFilterOptions,
  onAddNewPortfolio,
  className
}) => {
  const isMobile = useIsMobile();
  const businessUtils = useBusinessUtils();
  const { userData } = useFirebase(); // Changed from useSchema to useFirebase
  const auth = getAuth();
  const firebaseUser = auth.currentUser;
  
  // Add refs to prevent ANY logging
  const hasLoggedDebugInfo = useRef(true); // Set to true to prevent logging

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      status: [],
      portfolios: [],
      industries: []
    });
    setShowMobileSearch(false);
    setShowFilterButton(false);
    setShowFilterOptions(false);
  };

  // Cache role lookup to prevent redundant calculations
  const roleCache = useRef<Record<string, string | null>>({});
  
  // Find the user's role in a portfolio by matching the user's ID to a member
  const getUserRoleInPortfolio = (portfolio: Portfolio) => {
    if (!portfolio.members) return null;
    
    // Check cache first to avoid redundant calculations
    if (roleCache.current[portfolio.id]) {
      return roleCache.current[portfolio.id];
    }
    
    // Get current user ID from Firebase Auth (prioritize) or schema context
    const currentUserId = firebaseUser?.uid || userData?.id; // Changed currentUser to userData
    
    if (!currentUserId) {
      roleCache.current[portfolio.id] = null;
      return null;
    }
    
    // Check if user is a member by userId
    const userMember = portfolio.members.find(member => 
      member.userId === currentUserId
    );
    
    // If found by ID, cache and return the role
    if (userMember) {
      roleCache.current[portfolio.id] = userMember.role;
      return userMember.role;
    }
    
    // If not found by ID, check if user is a member by email
    if (firebaseUser?.email) {
      const userMemberByEmail = portfolio.members.find(member => 
        member.userId === firebaseUser.email
      );
      
      if (userMemberByEmail) {
        roleCache.current[portfolio.id] = userMemberByEmail.role;
        return userMemberByEmail.role;
      }
    }
    
    roleCache.current[portfolio.id] = null;
    return null;
  };

  // Function to format the portfolio role for display
  const formatPortfolioRole = (role: string | null): string => {
    if (!role) return "Portfolio Guest";
    
    switch (role) {
      case "Primary Owner":
        return "Portfolio Primary Owner";
      case "Owner":
        return "Portfolio Owner";
      case "Manager":
      case "Member":
        return "Portfolio Member";
      default:
        return `Portfolio ${role}`;
    }
  };

  // Get businesses that the user is a member of but not via a portfolio
  const { noPortfolioDummy, hasNoPortfolioBusinesses, isLoading } = useNoPortfolioBusinesses();

  const renderPortfolioSections = () => {
    // Create a combined array with the No Portfolio section first if it has businesses
    // Only include the No Portfolio section if it's not loading and has businesses
    const portfoliosToRender = (!isLoading && hasNoPortfolioBusinesses)
      ? [noPortfolioDummy as Portfolio, ...filteredPortfolios] // Added type assertion for noPortfolioDummy
      : filteredPortfolios;
    
    return portfoliosToRender.map((portfolio, index) => {
      // Get the user's role in this portfolio from the members array
      const portfolioRole = portfolio.id === "no-portfolio" 
        ? "Business Member" // Special role for No Portfolio section
        : getUserRoleInPortfolio(portfolio);
      
      // Format the role for display
      const displayRole = formatPortfolioRole(portfolioRole);

      // Resolve businesses for this portfolio
      const resolvedBusinesses: Business[] = [];
      if (portfolio.businessRefs) {
        portfolio.businessRefs.forEach(ref => {
          const business = allBusinesses.find(b => b.id === ref);
          // Ensure the found business is valid and has a string ID (as per Business schema)
          if (business && typeof business.id === 'string') { 
            resolvedBusinesses.push(business);
          }
        });
      }
      const businessesForThisPortfolio = resolvedBusinesses;
      
      return (
        <div key={portfolio.id}>
          {index > 0 && (
            <Separator className="my-4 bg-gray-200" />
          )}
          <PortfolioSection 
            title={portfolio.name} 
            portfolioRole={displayRole}
            businesses={businessesForThisPortfolio} // Pass resolved businesses
            isFirst={index === 0}
            portfolioId={portfolio.id}
            layoutMode={layoutMode}
            showFilterOptions={showFilterOptions}
            emptyState={portfolio.id === "empty-portfolio" ? (
              <EmptyPortfolioContent />
            ) : undefined}
          />
        </div>
      );
    });
  };

  const renderNoPortfoliosState = () => (
    <div className="text-center py-12 bg-white rounded-lg shadow-sm p-8 mt-4">
      <h3 className="text-xl font-semibold mb-2">No Business Portfolios Available</h3>
      <p className="text-gray-500 mb-6">Create your first business portfolio to get started</p>
      <Button 
        className="gap-2" 
        onClick={() => {
          if (onAddNewPortfolio) {
            onAddNewPortfolio();
          }
        }}
      >
        <PlusCircle size={16} />
        Create Portfolio
      </Button>
    </div>
  );

  const renderEmptyState = () => (
    <div className="text-center py-12">
      <p className="text-gray-500">No businesses found matching your search criteria.</p>
      {(searchTerm !== '' || Object.values(filters).some(f => f.length > 0)) && (
        <Button 
          variant="outline" 
          onClick={handleClearFilters}
          className="mt-4"
        >
          Clear Filters
        </Button>
      )}
    </div>
  );

  // If no portfolios exist at all and there are no businesses in the "No Portfolio" section, show a special message
  // But only if we're not still loading the No Portfolio businesses
  if (filteredPortfolios.length === 0 && !hasNoPortfolioBusinesses && !isLoading && 
      searchTerm === '' && Object.values(filters).every(f => f.length === 0)) {
    return renderNoPortfoliosState();
  }

  return (
    <div className={cn("space-y-2", isMobile ? "mt-0" : "mt-2", className)}>
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading businesses...</p>
        </div>
      ) : (
        (filteredPortfolios.length > 0 || hasNoPortfolioBusinesses) ? renderPortfolioSections() : renderEmptyState()
      )}
    </div>
  );
};

export default PortfolioList;
